import type { ScriptDocument } from "./types";

export const COLLABORATION_PROTOCOL_VERSION = 1;
export const MAX_COLLABORATION_MESSAGE_BYTES = 1_000_000;

export interface PlaybackSyncState {
  lineId: string;
  fallbackIndex: number;
  revealedWords: number;
  lineShown: boolean;
  speaking: boolean;
  sequence: number;
  sentAt: number;
}

export interface CollaborationParticipant {
  id: string;
  name: string;
  roleIds: string[];
  isHost: boolean;
}

export type CollaborationMessage =
  | { type: "hello"; protocolVersion: 1; sessionId: string; participantId: string; name: string; roleIds: string[] }
  | { type: "profile"; protocolVersion: 1; sessionId: string; participantId: string; name: string; roleIds: string[] }
  | { type: "roster"; protocolVersion: 1; sessionId: string; participants: CollaborationParticipant[] }
  | { type: "unlink-role"; protocolVersion: 1; sessionId: string; participantId: string; roleId: string }
  | { type: "script-snapshot"; protocolVersion: 1; sessionId: string; script: ScriptDocument; playlist: string[]; contentHash: string }
  | ({ type: "playback-state"; protocolVersion: 1; sessionId: string } & PlaybackSyncState)
  | { type: "ping" | "pong"; protocolVersion: 1; sessionId: string; sentAt: number }
  | { type: "participant-left"; protocolVersion: 1; sessionId: string; participantId: string }
  | { type: "error"; protocolVersion: 1; sessionId: string; message: string };

export interface SignallingBundle {
  version: 1;
  kind: "offer" | "answer";
  sessionId: string;
  peerId: string;
  description: RTCSessionDescriptionInit;
}

interface Envelope { version: 1; encoding: "gzip" | "plain"; checksum: string; payload: string }

export function randomId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  return toBase64Url(bytes);
}

export function contentHash(value: unknown): string {
  return checksum(JSON.stringify(value));
}

export async function encodeSignallingBundle(bundle: SignallingBundle): Promise<string> {
  validateSignallingBundle(bundle);
  const source = new TextEncoder().encode(JSON.stringify(bundle));
  const compressed = await compress(source);
  const envelope: Envelope = {
    version: 1,
    encoding: compressed === source ? "plain" : "gzip",
    checksum: checksumBytes(source),
    payload: toBase64Url(compressed),
  };
  return `LBL1.${toBase64Url(new TextEncoder().encode(JSON.stringify(envelope)))}`;
}

export async function decodeSignallingBundle(value: string): Promise<SignallingBundle> {
  const trimmed = value.trim();
  if (!trimmed.startsWith("LBL1.")) throw new Error("This is not a LineByLine collaboration code.");
  let envelope: Envelope;
  try {
    envelope = JSON.parse(new TextDecoder().decode(fromBase64Url(trimmed.slice(5)))) as Envelope;
  } catch {
    throw new Error("The collaboration code is malformed.");
  }
  if (envelope.version !== 1 || (envelope.encoding !== "gzip" && envelope.encoding !== "plain") || typeof envelope.payload !== "string") {
    throw new Error("This collaboration code version is not supported.");
  }
  let bytes = fromBase64Url(envelope.payload);
  if (envelope.encoding === "gzip") bytes = await decompress(bytes);
  if (bytes.byteLength > MAX_COLLABORATION_MESSAGE_BYTES) throw new Error("The collaboration code is too large.");
  if (checksumBytes(bytes) !== envelope.checksum) throw new Error("The collaboration code is damaged or incomplete.");
  let bundle: unknown;
  try { bundle = JSON.parse(new TextDecoder().decode(bytes)); } catch { throw new Error("The collaboration code payload is malformed."); }
  validateSignallingBundle(bundle);
  return bundle;
}

export function encodeMessage(message: CollaborationMessage): string {
  const encoded = JSON.stringify(message);
  if (new TextEncoder().encode(encoded).byteLength > MAX_COLLABORATION_MESSAGE_BYTES) throw new Error("The collaboration message is too large.");
  return encoded;
}

export function decodeMessage(value: string, expectedSessionId: string): CollaborationMessage {
  if (new TextEncoder().encode(value).byteLength > MAX_COLLABORATION_MESSAGE_BYTES) throw new Error("The collaboration message is too large.");
  let message: unknown;
  try { message = JSON.parse(value); } catch { throw new Error("A collaboration message is not valid JSON."); }
  if (!record(message) || message.protocolVersion !== 1 || message.sessionId !== expectedSessionId || typeof message.type !== "string") {
    throw new Error("A collaboration message has an invalid session or protocol version.");
  }
  const allowed = new Set(["hello", "profile", "roster", "unlink-role", "script-snapshot", "playback-state", "ping", "pong", "participant-left", "error"]);
  if (!allowed.has(message.type)) throw new Error("An unknown collaboration message was rejected.");
  if (message.type === "playback-state") {
    if (typeof message.lineId !== "string" || !finiteInteger(message.fallbackIndex) || !finiteInteger(message.revealedWords) || !finiteInteger(message.sequence) || typeof message.lineShown !== "boolean" || typeof message.speaking !== "boolean" || typeof message.sentAt !== "number") throw new Error("A playback update is malformed.");
  }
  if (message.type === "script-snapshot") {
    if (!record(message.script) || !Array.isArray(message.playlist) || !message.playlist.every((id) => typeof id === "string") || typeof message.contentHash !== "string") throw new Error("A script snapshot is malformed.");
  }
  if (message.type === "hello" || message.type === "profile") validateProfile(message);
  if (message.type === "roster") {
    if (!Array.isArray(message.participants) || message.participants.length > 9) throw new Error("A participant roster is malformed.");
    message.participants.forEach(validateParticipant);
  }
  if (message.type === "unlink-role" && (typeof message.participantId !== "string" || typeof message.roleId !== "string")) throw new Error("A role unlink message is malformed.");
  return message as CollaborationMessage;
}

export function validateStunServers(values: string[]): string[] {
  const unique = new Set<string>();
  for (const raw of values) {
    const value = raw.trim();
    if (!value) continue;
    if (!/^stuns?:[^\s]+$/i.test(value)) throw new Error(`Invalid STUN URL: ${value}`);
    unique.add(value);
  }
  if (!unique.size) throw new Error("Add at least one STUN server.");
  return [...unique];
}

function validateProfile(value: Record<string, unknown>): void {
  if (typeof value.participantId !== "string" || typeof value.name !== "string" || !value.name.trim() || value.name.length > 80 || !validRoleIds(value.roleIds)) throw new Error("A participant profile is malformed.");
}
function validateParticipant(value: unknown): void {
  if (!record(value) || typeof value.id !== "string" || typeof value.name !== "string" || !value.name.trim() || value.name.length > 80 || typeof value.isHost !== "boolean" || !validRoleIds(value.roleIds)) throw new Error("A participant roster entry is malformed.");
}
function validRoleIds(value: unknown): value is string[] { return Array.isArray(value) && value.length <= 50 && value.every((id) => typeof id === "string" && id.length <= 160); }

function validateSignallingBundle(value: unknown): asserts value is SignallingBundle {
  if (!record(value) || value.version !== 1 || (value.kind !== "offer" && value.kind !== "answer") || typeof value.sessionId !== "string" || !value.sessionId || typeof value.peerId !== "string" || !value.peerId || !record(value.description)) throw new Error("The collaboration code has invalid signalling data.");
  const description = value.description;
  if (description.type !== value.kind || typeof description.sdp !== "string" || !description.sdp || description.sdp.length > 250_000) throw new Error("The collaboration code has an invalid WebRTC description.");
}

async function compress(source: Uint8Array): Promise<Uint8Array> {
  if (!("CompressionStream" in globalThis)) return source;
  const stream = new Blob([new Uint8Array(source)]).stream().pipeThrough(new CompressionStream("gzip"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function decompress(source: Uint8Array): Promise<Uint8Array> {
  if (!("DecompressionStream" in globalThis)) throw new Error("This browser cannot decompress the collaboration code.");
  try {
    const stream = new Blob([new Uint8Array(source)]).stream().pipeThrough(new DecompressionStream("gzip"));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  } catch { throw new Error("The collaboration code could not be decompressed."); }
}

function checksum(value: string): string { return checksumBytes(new TextEncoder().encode(value)); }
function checksumBytes(bytes: Uint8Array): string {
  let hash = 0x811c9dc5;
  for (const byte of bytes) { hash ^= byte; hash = Math.imul(hash, 0x01000193); }
  return (hash >>> 0).toString(16).padStart(8, "0");
}
function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}
function fromBase64Url(value: string): Uint8Array {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  let binary: string;
  try { binary = atob(base64); } catch { throw new Error("The collaboration code uses invalid encoding."); }
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}
function record(value: unknown): value is Record<string, unknown> { return typeof value === "object" && value !== null && !Array.isArray(value); }
function finiteInteger(value: unknown): value is number { return typeof value === "number" && Number.isInteger(value) && value >= 0; }
